// pages/Editor/components/tree/StepsTree.tsx
import * as React from 'react';
import { useEffect } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { LoadingOverlay } from '@mantine/core';
import useReorderSteps from '@/hooks/api/steps/useReorderSteps';
import { flattenTree, getProjection, removeDescendants } from './treeUtils';
import { dropAnimation, measuring } from './dndConfig';
import { useTreeDerived } from './useTreeDerived';
import type { StepsTreeProps } from './types';
import StepRenderer from '@/pages/Editor/components/EditorStepCard/StepRenderer.tsx';
import { moveStepImmutable } from '@/pages/Editor/components/tree/moveStepHelpers';
import { buildStepDisabledInfoById, getActiveConditionId } from '@/utils/stepUtils.ts';
import { StepType } from '@/types/automationFields.ts';
import AddStepButton from '@/pages/Editor/components/AddStepButton/AddStepButton.tsx';

type Props = StepsTreeProps;

export default function StepsTree({
  steps,
  indentationWidth = 40,
  showRootAddButton = false,
  canAdd = true,
  canReorder = true,
}: Props) {
  const [currentSteps, setCurrentSteps] = React.useState(steps);

  useEffect(() => setCurrentSteps(steps), [steps]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const [activeId, setActiveId] = React.useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = React.useState<UniqueIdentifier | null>(null);
  const [offsetLeft, setOffsetLeft] = React.useState(0);

  const [activeConditionIds, setActiveConditionIds] = React.useState<Record<string, string>>({});

  const baseFlat = React.useMemo(
    () => flattenTree(currentSteps, activeConditionIds),
    [activeConditionIds, currentSteps],
  );
  const flat = React.useMemo(
    () => (activeId ? removeDescendants(baseFlat, [activeId]) : baseFlat),
    [baseFlat, activeId],
  );

  const { meta, addAfterById } = useTreeDerived(flat, activeConditionIds);
  const disabledInfoById = React.useMemo(
    () => buildStepDisabledInfoById(currentSteps),
    [currentSteps],
  );

  const projected =
    activeId && overId
      ? getProjection(flat, activeId, overId, offsetLeft, indentationWidth, activeConditionIds)
      : null;

  const sortedIds = React.useMemo(() => flat.map(({ id }) => id), [flat]);
  const activeItem = activeId ? (flat.find(({ id }) => id === activeId) ?? null) : null;

  const { mutate: reorderSteps, isPending } = useReorderSteps();

  function handleDragStart({ active: { id } }: DragStartEvent) {
    if (!canReorder) return;
    if (meta.byId.get(String(id))?.step.step_type === StepType.LocalSubflowCall) return;
    setActiveId(id);
    setOverId(id);
    document.body.style.setProperty('cursor', 'grabbing');
  }
  function handleDragMove({ delta }: DragMoveEvent) {
    setOffsetLeft(delta.x);
  }
  function handleDragOver({ over }: DragOverEvent) {
    setOverId(over?.id ?? null);
  }
  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!canReorder) return;
    document.body.style.setProperty('cursor', '');
    setOverId(null);
    setActiveId(null);
    setOffsetLeft(0);
    if (!projected || !over) return;

    const movedId = String(active.id);
    if (meta.byId.get(movedId)?.step.step_type === StepType.LocalSubflowCall) return;
    const newParent = projected.parentId ? String(projected.parentId) : null;
    const newIndex = projected.index;

    const parentStep = (newParent ? meta.byId.get(newParent) : undefined)?.step;
    const conditionId = getActiveConditionId(parentStep, activeConditionIds || {});

    const optimistic = moveStepImmutable(currentSteps, movedId, newParent, newIndex, conditionId);
    const previousSteps = currentSteps;
    setCurrentSteps(optimistic);

    reorderSteps(
      {
        step_id: movedId,
        new_parent_step_id: newParent ?? undefined,
        new_step_index: newIndex,
        condition_id: conditionId,
      },
      {
        onError: () => {
          setCurrentSteps(previousSteps);
        },
      },
    );
  }

  function handleDragCancel() {
    document.body.style.setProperty('cursor', '');
    setOverId(null);
    setActiveId(null);
    setOffsetLeft(0);
  }

  const triggerStep = currentSteps.find((step) => step.step_type === 'trigger');

  return (
    <>
      {/*<AddStepButton*/}
      {/*  addSiblingParents={[{ parentStep: undefined, insertIndex: 0 }]}*/}
      {/*  alwaysShow={flat.length === 0}*/}
      {/*/>*/}
      <div style={{ position: 'relative', width: '100%', marginTop: '30px' }}>
        <LoadingOverlay visible={isPending} zIndex={1000} />
        {showRootAddButton && canAdd && (
          <AddStepButton
            addSiblingParents={[{ parentStep: undefined, insertIndex: 0 }]}
            alwaysShow={currentSteps.length === 0}
          />
        )}

        {triggerStep && (
          <StepRenderer
            step={triggerStep}
            index={meta.indexById.get(triggerStep.step_id) ?? 0}
            parentSteps={meta.parentStepsById.get(triggerStep.step_id) || []}
            addSiblingParents={addAfterById.get(triggerStep.step_id) ?? []}
            isDragPreview={false}
            isLastStep={steps.length < 2}
            disabledInfo={disabledInfoById.get(triggerStep.step_id)}
            canAdd={canAdd}
            canReorder={canReorder}
          />
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          measuring={measuring}
          onDragStart={canReorder ? handleDragStart : undefined}
          onDragMove={canReorder ? handleDragMove : undefined}
          onDragOver={canReorder ? handleDragOver : undefined}
          onDragEnd={canReorder ? handleDragEnd : undefined}
          onDragCancel={canReorder ? handleDragCancel : undefined}
        >
          <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
            {flat.map(({ id, step, depth }, idx) => {
              if (step.step_type === 'trigger') return null; // already rendered above
              const rowDepth = id === activeId && projected ? projected.depth : depth;
              const index = meta.indexById.get(id) ?? 0;
              const parentSteps = meta.parentStepsById.get(id) || [];
              const addAfterTargets = addAfterById.get(id) ?? [];

              return (
                <div key={String(id)} style={{ paddingLeft: rowDepth * indentationWidth }}>
                  <StepRenderer
                    step={step}
                    index={index}
                    parentSteps={parentSteps}
                    addSiblingParents={addAfterTargets}
                    isDragPreview={false}
                    isLastStep={idx === flat.length - 1}
                    activeConditionIds={activeConditionIds}
                    setActiveConditionIds={setActiveConditionIds}
                    disabledInfo={disabledInfoById.get(step.step_id)}
                    canAdd={canAdd}
                    canReorder={canReorder}
                  />
                </div>
              );
            })}

            <DragOverlay dropAnimation={dropAnimation}>
              {activeId && activeItem && (
                <div
                  style={{
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    background: 'var(--mantine-color-body)',
                    borderRadius: 8,
                    width: 300,
                  }}
                >
                  <StepRenderer
                    step={activeItem.step}
                    index={projected ? projected.index : 0}
                    parentSteps={[]}
                    addSiblingParents={[]} // usually not shown in preview
                    isDragPreview={true}
                    disabledInfo={disabledInfoById.get(activeItem.step.step_id)}
                    canAdd={false}
                    canReorder={false}
                  />
                </div>
              )}
            </DragOverlay>
          </SortableContext>
        </DndContext>

      </div>
    </>
  );
}
